import { useMemo } from 'react';

import { Feature } from '@turf/turf';

import { SignatureReadModel } from '@src/clients/atlas/api';
import { createOptimizedContext } from '@src/contexts/createOptimizedContext';
import { useQueryParams } from '@src/hooks';

import { useMapViewSelector } from '../mapView/context';

interface AoiContext {
  aoi: Feature[] | null;
}

interface AoiFloatingComponentsContext {
  colorMapObject: {
    rescale: Array<number>;
    colormap: string;
    type: string;
    buckets: object;
  } | null;
  dates: Array<string> | null;
  date: string | null;
}

export enum AoiMapPrimaryActionsTools {
  AOI_INFO = 'aoi-info',
  SCENES = 'scenes',
  VISUALIZATION = 'visualization',
  SPECTRAL_GRAPH = 'spectral-graph',
  INSIGHTS = 'insights',
  VIEW_SELECTOR = 'view-selector'
}

export enum VisualizationStage {
  LIST = 'list',
  ADD_PRESET = 'add_preset',
  ADD_CUSTOM = 'add_custom',
  DETAILS = 'details',
  CREATE_INDEX = 'create_index'
}

export type VisualizationQueryContext = {
  stage: VisualizationStage;
  indiceId: string;
  date: string;
};

export enum InsightsStage {
  LIST = 'list',
  INPUT = 'input',
  OUTPUT = 'output',
  MODEL_LIST = 'modelList'
}

export type InsightsQueryContext = {
  stage: InsightsStage;
  inferenceId: string;
  activeModelId: string;
};

export type SpectralPoint = {
  color: string;
  coordinates: [number, number];
  id: string;
  name: string;
  response: SignatureReadModel;
};

interface SpectralContext {
  points: Array<SpectralPoint>;
  colors: Array<string>;
}

export function useAoiQueryParams(
  type: 'visualization'
): ReturnType<typeof useQueryParams<VisualizationQueryContext>>;
export function useAoiQueryParams(
  type: 'insights'
): ReturnType<typeof useQueryParams<InsightsQueryContext>>;
export function useAoiQueryParams(
  type?: undefined
): ReturnType<typeof useQueryParams>;
export function useAoiQueryParams(type?: 'visualization' | 'insights') {
  const toolKey = useMapViewSelector(store => store.toolKey);

  const [keys, selectors] = useMemo(() => {
    const selectors = [toolKey].filter(d => d !== null);

    if (type === 'insights') {
      const keys: (keyof InsightsQueryContext)[] = [
        'stage',
        'inferenceId',
        'activeModelId'
      ];
      return [keys, selectors];
    }

    if (type === 'visualization') {
      const keys: (keyof VisualizationQueryContext)[] = [
        'stage',
        'indiceId',
        'date'
      ];
      return [keys, selectors];
    }

    return [[], selectors];
  }, [type, toolKey]);

  return useQueryParams(keys, selectors);
}

export const {
  Provider: AoiProvider,
  useStoreSelector: useAoiSelector,
  useStoreDispatch: useAoiDispatch
} = createOptimizedContext<AoiContext>({
  aoi: null
});

export const {
  Provider: SpectralProvider,
  useStoreSelector: useSpectralSelector,
  useStoreDispatch: useSpectralDispatch,
  useStore: useSpectralStore
} = createOptimizedContext<SpectralContext>({
  points: [],
  colors: []
});

export const {
  Provider: AoiFloatingComponentsProvider,
  useStoreSelector: useAoiFloatingComponentsSelector,
  useStoreDispatch: useAoiFloatingComponentsDispatch,
  useStore: useAoiFloatingComponentsStore
} = createOptimizedContext<AoiFloatingComponentsContext>({
  colorMapObject: null,
  dates: null,
  date: null
});
